import { Alert, Box, Button, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { updateDocumentAgreement, updateInfoAccuracy } from '@/api/user';

import { StatusIcon } from '@/components/DataDisplay/StatusIcon';
import { Checkbox } from '@/components/Form/Checkbox';
import { Icon } from '@/components/Icon/Icon';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectAdobeSignFormFeature } from '@/redux/configs';

import { ConsentDocument } from '@/types/api/user';
import { DocumentStatus } from '@/types/document';

import { getAPIErrorMessage } from '@/utils/api';

import { ReviewDocumentDrawer } from './ReviewDocumentDrawer';
import {
  getConsentDocumentsQueryKey,
  useConsentDocuments,
} from './useConsentDocuments';

import {
  DocumentAgreedTitle,
  DocumentLink,
  DocumentTimeline,
  DocumentTimelineDot,
  DocumentTimelineItem,
  DocumentTimelineLine,
} from './ReviewAndSubmit.styled';

type ReviewAndSubmitContentProps = {
  disabled?: boolean;
  /** Increment/change to clear the local confirmation checkbox */
  resetAgreementKey?: number;
};

export function ReviewAndSubmitContent({
  disabled,
  resetAgreementKey = 0,
}: Readonly<ReviewAndSubmitContentProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const isAdobeSignFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const queryClient = useQueryClient();

  const [isAgreed, setIsAgreed] = useState(false);
  const [activeDocument, setActiveDocument] = useState<ConsentDocument | null>(
    null,
  );

  const {
    data: consentDocuments,
    isLoading: isLoadingDocuments,
    error: consentDocumentsError,
  } = useConsentDocuments(!isAdobeSignFormEnabled);

  const isConfirmed = !!user?.infoAccuracyConfirmed;
  const allDocumentsAgreed =
    consentDocuments?.length &&
    consentDocuments.every(document => document.acceptedVersion);

  const {
    mutate: mutateInfoAccuracy,
    isPending: isSubmitting,
    error: submitError,
    reset: resetSubmit,
  } = useMutationWithUserData({
    mutationFn: updateInfoAccuracy,
  });

  const {
    mutate: mutateDocumentAgreement,
    isPending: isUpdatingAgreement,
    error: agreementError,
    reset: resetAgreement,
  } = useMutation({
    mutationFn: updateDocumentAgreement,
    onSuccess: (_data, payload) => {
      const agreedDocument = payload.documents[0];

      queryClient.setQueryData<ConsentDocument[]>(
        getConsentDocumentsQueryKey(user?.operationId),
        currentDocuments =>
          currentDocuments?.map(document =>
            document.consentType === agreedDocument.consentType
              ? {
                  ...document,
                  acceptedVersion: agreedDocument.agreed
                    ? agreedDocument.version
                    : null,
                }
              : document,
          ),
      );
    },
  });

  const isPending = isLoadingDocuments || isSubmitting || isUpdatingAgreement;
  const error = submitError || agreementError || consentDocumentsError;

  useEffect(() => {
    if (disabled) {
      setIsAgreed(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!isConfirmed) {
      setIsAgreed(false);
    }
  }, [isConfirmed, user?.id]);

  useEffect(() => {
    setIsAgreed(false);
  }, [resetAgreementKey]);

  const handleAgreementChange = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setIsAgreed(checked);
  };

  const onSubmit = () => {
    resetSubmit();
    resetAgreement();
    mutateInfoAccuracy({
      infoAccuracyConfirmed: true,
    });
  };

  const closeDocumentDrawer = () => {
    setActiveDocument(null);
  };

  const handleDocumentAgree = () => {
    if (!activeDocument) {
      return;
    }

    resetAgreement();
    closeDocumentDrawer();
    mutateDocumentAgreement({
      documents: [
        {
          consentType: activeDocument.consentType,
          agreed: true,
          version: activeDocument.currentVersion,
        },
      ],
    });
  };

  const activeDocumentIndex =
    activeDocument && consentDocuments?.length
      ? consentDocuments.findIndex(
          document => document.consentType === activeDocument.consentType,
        ) + 1
      : 0;

  const renderDocumentTitle = (document: ConsentDocument) => {
    return (
      <Trans
        i18nKey={`moreInfo.form.reviewAndSubmit.consentDocuments.${document.consentType}`}
        components={{ i: <i /> }}
      />
    );
  };

  return (
    <>
      {isPending && <FullPageLoaderOverlay />}

      <DocumentTimeline>
        <DocumentTimelineLine />
        {consentDocuments?.map(document => {
          const isDocumentAgreed = !!document.acceptedVersion;

          return (
            <DocumentTimelineItem key={document.consentType}>
              {isDocumentAgreed ? (
                <StatusIcon value={DocumentStatus.Approved} size={32} />
              ) : (
                <DocumentTimelineDot />
              )}
              {isDocumentAgreed ? (
                <DocumentAgreedTitle>
                  {renderDocumentTitle(document)}
                </DocumentAgreedTitle>
              ) : (
                <DocumentLink
                  type="button"
                  onClick={() => setActiveDocument(document)}
                >
                  {renderDocumentTitle(document)}
                </DocumentLink>
              )}
            </DocumentTimelineItem>
          );
        })}
      </DocumentTimeline>

      <Checkbox
        checked={isConfirmed || isAgreed}
        onChange={handleAgreementChange}
        disabled={disabled || isConfirmed || !allDocumentsAgreed}
        label={t('moreInfo.form.reviewAndSubmit.checkbox.label')}
        labelProps={{
          color: allDocumentsAgreed ? 'text.primary' : 'text.disabled',
        }}
      />

      {error && (
        <Box my={1.5}>
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        </Box>
      )}

      <Stack mt={2}>
        <Button
          fullWidth
          onClick={onSubmit}
          disabled={disabled || isConfirmed || !isAgreed || !allDocumentsAgreed}
        >
          {t('moreInfo.form.reviewAndSubmit.button.submit')}
        </Button>
      </Stack>

      <ReviewDocumentDrawer
        open={!!activeDocument}
        document={activeDocument}
        currentIndex={activeDocumentIndex}
        totalDocuments={consentDocuments?.length ?? 0}
        onClose={closeDocumentDrawer}
        onAgree={handleDocumentAgree}
        onDecline={closeDocumentDrawer}
      />
    </>
  );
}
